import React from "react";
import { MessageCircle, Phone, ShieldCheck, Star, ZoomIn } from "lucide-react";

const PMM_LOGO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_89891435-6db1-403c-a368-9f416e4fb932/artifacts/ksi5basw_WhatsApp%20Image%202026-08-13%20at%207.58.19%20AM.jpeg";

const GoldRule = ({ className = "" }) => (
  <div className={`h-[2px] bg-gradient-to-r from-yellow-600 via-yellow-300 to-yellow-600 ${className}`} />
);

/* Kad profil pengurus PMM (berkod, dari poster "Team PMM Setta Group Holdings").
 * Digunakan untuk Pengurus Kelas Online (Cikgu Zarith) & Kelas Fizikal (Cikgu Shafie). */
export default function ManagerProfileCard({ ms, manager, onOpen, photo, poster, fullName, role, title, subtitle, testId, accent = "navy", nameSerif = false, badge = "Team PMM", cta, photoClass = "sm:right-[-8%] sm:h-[96%]" }) {
  // cta (pilihan): { href, label, kind: "tel" | "wa" } — lalai WhatsApp pengurus
  const btn = cta || { href: manager.wa, label: `WhatsApp ${manager.name} ${manager.phoneDisplay}`, kind: "wa" };
  const BtnIcon = btn.kind === "tel" ? Phone : MessageCircle;
  return (
    <div data-testid={`${testId}-card`}>
      <p className={`text-center font-display text-xs font-bold uppercase tracking-widest ${accent === "red" ? "text-orange-600" : "text-blue-800"}`}>{title}</p>
      {subtitle && <p className="mt-1 text-center text-xs font-semibold text-navy-900">{subtitle}</p>}

      <button
        type="button"
        onClick={() => onOpen(poster)}
        className="group relative mt-3 block w-full overflow-hidden rounded-2xl border-2 border-yellow-400/80 bg-gradient-to-br from-white via-[#f7f8fc] to-[#e9edf7] text-left shadow-xl shadow-navy-900/15 transition-shadow duration-300 hover:shadow-2xl hover:shadow-yellow-500/25"
        data-testid={`${testId}-poster`}
      >
        {/* Bingkai pepenjuru (navy + emas + oren) */}
        <div className="pointer-events-none absolute -right-10 -top-6 h-[120%] w-16 rotate-[14deg] bg-gradient-to-b from-navy-900 to-navy-700" />
        <div className="pointer-events-none absolute -right-2 -top-6 h-[120%] w-2.5 rotate-[14deg] bg-gradient-to-b from-yellow-300 via-yellow-500 to-yellow-700" />
        <div className="pointer-events-none absolute -bottom-10 -left-8 h-24 w-[70%] -rotate-[14deg] bg-gradient-to-r from-navy-900 to-navy-700" />
        <div className="pointer-events-none absolute -left-8 bottom-12 h-2.5 w-[72%] -rotate-[14deg] bg-gradient-to-r from-yellow-700 via-yellow-400 to-yellow-200" />
        <div className="pointer-events-none absolute -left-8 bottom-[3.9rem] h-1 w-[60%] -rotate-[14deg] bg-gradient-to-r from-orange-700 to-orange-500" />

        <div className="relative grid grid-cols-1 sm:min-h-[30rem] sm:grid-cols-[1.05fr_1fr]">
          {/* Kiri: jenama + nama + jawatan */}
          <div className="relative z-10 flex flex-col px-5 pb-20 pt-4 sm:px-5 sm:pb-24 sm:pt-6">
            <div className="flex flex-col items-center text-center">
              <div className="rounded-2xl bg-gradient-to-br from-yellow-200 via-yellow-500 to-yellow-700 p-0.5 shadow-lg">
                <img src={PMM_LOGO} alt="Logo PMM" className="h-12 w-12 rounded-[0.9rem] object-cover sm:h-16 sm:w-16" loading="lazy" />
              </div>
              <div className="mt-2 font-display text-xs font-extrabold uppercase leading-tight tracking-wide text-navy-900 sm:text-sm">
                Setta Group Holdings<br />Sdn. Bhd.
              </div>
              <GoldRule className="mt-1 w-full" />
              <div className="mt-1 text-[9px] font-bold uppercase tracking-[0.2em] text-navy-800">Membaca · Mengubah · Masa Depan</div>
            </div>

            <GoldRule className="mt-4" />
            <div className={nameSerif ? "mt-2 font-serif-display text-[1.7rem] font-extrabold uppercase leading-[0.95] text-navy-900 sm:text-[2.1rem]" : "mt-2 font-display text-lg font-extrabold uppercase leading-[1.1] text-navy-900 sm:text-xl"} data-testid={`${testId}-fullname`}>
              {fullName.map((line, i) => (<React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>))}
            </div>
            <GoldRule className="mt-2" />
            <div className="mt-2">
              <span className="inline-block rounded-md bg-gradient-to-r from-orange-700 to-orange-500 px-2 py-0.5 font-display text-xs font-extrabold uppercase tracking-wide text-white shadow sm:text-sm" data-testid={`${testId}-role`}>
                {ms ? role.ms : role.en}
              </span>
              <div className="mt-1 text-[11px] font-semibold uppercase leading-snug text-navy-800 sm:text-xs">
                Program Membaca PMM<br />Setta Group Holdings Sdn. Bhd.
              </div>
            </div>
            <GoldRule className="mt-2" />

            <div className="mt-3 font-script text-2xl font-bold leading-[1.05] text-transparent sm:text-3xl" style={{ backgroundImage: "linear-gradient(180deg,#fcbf1e,#d08400)", WebkitBackgroundClip: "text", backgroundClip: "text" }}>
              Baca Ilmu<br />Bina Masa Depan
            </div>
          </div>

          {/* Kanan: foto */}
          <div className="relative order-first h-80 border-b-2 border-yellow-400/60 sm:order-none sm:h-auto sm:border-b-0">
            <div className="absolute inset-x-0 bottom-0 top-10 rounded-full bg-[radial-gradient(circle_at_50%_40%,rgba(255,255,255,0.95),rgba(255,255,255,0)_70%)]" />
            <img
              src={photo}
              alt={`${manager.name} - ${title}`}
              loading="lazy"
              className={`absolute bottom-0 left-1/2 z-[5] h-[96%] w-auto max-w-none -translate-x-1/2 sm:left-auto sm:translate-x-0 ${photoClass} object-contain object-bottom drop-shadow-2xl transition-transform duration-700 group-hover:scale-[1.03]`}
            />
            <span className="absolute right-3 top-3 z-10 grid h-8 w-8 place-items-center rounded-full bg-navy-950/75 text-yellow-300 opacity-0 ring-1 ring-yellow-400/60 transition-opacity duration-300 group-hover:opacity-100">
              <ZoomIn className="h-4 w-4" />
            </span>
          </div>
        </div>

        {/* Pita bawah */}
        <div className="absolute inset-x-0 bottom-0 z-10 flex items-center justify-between gap-2 bg-gradient-to-r from-navy-950/95 via-navy-900/95 to-navy-950/95 px-3 py-2 text-white backdrop-blur sm:px-4">
          <span className="flex min-w-0 items-center gap-1.5 font-display text-[11px] font-extrabold uppercase tracking-wide sm:text-sm">
            <Star className="h-3.5 w-3.5 shrink-0 fill-yellow-400 text-yellow-400" />
            <span className="truncate">{manager.name}</span>
          </span>
          <span className="flex shrink-0 items-center gap-1 text-[10px] font-bold text-yellow-300 sm:text-xs">
            <ShieldCheck className="h-3.5 w-3.5" /> {badge}
          </span>
        </div>
      </button>

      <a
        href={btn.href}
        {...(btn.kind === "tel" ? {} : { target: "_blank", rel: "noopener noreferrer" })}
        className={`mt-3 flex items-center justify-center gap-2 rounded-full px-6 py-3 font-display text-sm font-extrabold uppercase tracking-wide text-white shadow-lg transition-colors ${accent === "red" ? "bg-orange-500 hover:bg-orange-600" : "bg-blue-800 hover:bg-blue-900"}`}
        data-testid={`${testId}-call-btn`}
      >
        <BtnIcon className="h-5 w-5" />
        {btn.label}
      </a>
    </div>
  );
}
